import React, { useState, useEffect, useRef } from "react";
import { Bell, X, User, Clock, Check } from "lucide-react";
import apiService from "../services/api";

interface Notification {
  id: string;
  title: string;
  message: string;
  leadId?: string;
  lead?: {
    id: string;
    firstName?: string;
    lastName?: string;
  };
  isRead: boolean;
  createdAt: string;
}

interface NotificationCenterProps {
  onNavigateToLead: (leadId: string) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  onNavigateToLead,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousNotificationIdsRef = useRef<Set<string>>(new Set());
  // Track the count when bell was last clicked to calculate new notifications
  // null means bell hasn't been clicked yet, so show all current notifications
  const countWhenBellClickedRef = useRef<number | null>(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      console.log("🔔 Fetching notifications...");
      const [notificationsData, countData] = await Promise.all([
        apiService.getNotifications(20),
        apiService.getUnreadCount(),
      ]);
      console.log("📊 Notifications data:", notificationsData);
      console.log("🔢 Unread count:", countData);

      // Only update if there are new notifications (different IDs)
      const currentIds = new Set(notificationsData.map((n) => n.id));
      const previousIds = previousNotificationIdsRef.current;

      // Check if there are any new notification IDs
      const hasNewNotifications = notificationsData.some(
        (n) => !previousIds.has(n.id)
      );

      if (hasNewNotifications || previousIds.size === 0) {
        // Update notifications if there are new ones or this is the first fetch
        setNotifications(notificationsData);
        previousNotificationIdsRef.current = currentIds;
      }

      setUnreadCount(countData);
    } catch (error) {
      console.error("❌ Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Poll for new notifications every 5 minutes (300000ms)
    const interval = setInterval(fetchNotifications, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.leadId) {
      onNavigateToLead(notification.leadId);
    }

    if (!notification.isRead) {
      try {
        await apiService.markNotificationAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    setIsOpen(false);
  };

  const handleDismissNotification = async (
    notificationId: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    try {
      await apiService.dismissNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    }
  };

  const handleDismissAll = async () => {
    try {
      await apiService.dismissAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to dismiss all notifications:", error);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getLeadName = (notification: Notification) => {
    if (notification.lead) {
      return (
        `${notification.lead.firstName || ""} ${
          notification.lead.lastName || ""
        }`.trim() || "Lead"
      );
    }
    return "Lead";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          // When bell is clicked, update the reference count to current count
          // This will hide the badge and reset the "new notifications" counter
          if (!isOpen) {
            // Opening the dropdown - reset the count reference
            countWhenBellClickedRef.current = unreadCount;
          }
        }}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="w-5 h-5" />
        {(() => {
          // If bell hasn't been clicked yet, show all current notifications
          // If bell was clicked, only show new notifications (difference)
          const displayCount =
            countWhenBellClickedRef.current === null
              ? unreadCount
              : unreadCount - countWhenBellClickedRef.current;

          // Only show badge if there are notifications to display
          if (displayCount > 0) {
            return (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {displayCount > 9 ? "9+" : displayCount}
              </span>
            );
          }
          return null;
        })()}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Notifications
              </h3>
              {notifications.length > 0 && (
                <button
                  onClick={handleDismissAll}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Dismiss all
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <i className="fa-solid fa-triangle-exclamation text-4xl text-gray-400" aria-hidden />
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (  
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.isRead ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            notification.isRead ? "bg-gray-100" : "bg-blue-100"
                          }`}
                        >
                          {notification.isRead ? (
                            <Check className="w-4 h-4 text-gray-600" />
                          ) : (
                            <Bell className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p
                            className={`text-sm font-medium ${
                              notification.isRead
                                ? "text-gray-900"
                                : "text-blue-900"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) =>
                              handleDismissNotification(notification.id, e)
                            }
                            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.leadId && (
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <User className="w-3 h-3 mr-1" />
                            <span>{getLeadName(notification)}</span>
                          </div>
                        )}
                        <div className="flex items-center mt-2 text-xs text-gray-400">
                          <Clock className="w-3 h-3 mr-1" />
                          <span>{formatTimeAgo(notification.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
